import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { usePublic } from '@/contexts/PublicContext';
import ChartAnalysisFeedbackListModal from '@/features/(charts)/chart-renderer/ui/chart-feedback-list/ChartAnalysisFeedbackListModal';
import ChartSegmentFeedbackListModal from '@/features/(charts)/chart-renderer/ui/chart-feedback-list/ChartSegmentFeedbackListModal';
import CompanyDetailModal from '@/features/Account/components/CompanyDetail/CompanyDetailModal';
import AgentDetailModal from '@/features/Agent/components/AgentDetail/AgentDetailModal';
import CustomerDetailModal from '@/features/Contact/CustomerDetail/CustomerDetailModal';
import FeedbackBlockListModal from '@/features/Feedback/components/FeedbackListModal/FeedbackBlockListModal';
import FeedbackListModal from '@/features/Feedback/components/FeedbackListModal/FeedbackListModal';
import { FeedbackDetailProvider } from '@/features/Feedback/FeedbackDetail/context/FeedbackDetailContext';
import FeedbackDetailModal from '@/features/Feedback/FeedbackDetail/FeedbackDetailModal/FeedbackDetailModal';
import GlobalSearchSummaryFeedbackListModal from '@/features/GlobalSearch/components/GlobalSearchSummaryFeedbackListModal/GlobalSearchSummaryFeedbackListModal';
import { CategoryMentionListModal } from '@/features/hey-syncly/ui/mention-list-modal/category-mention-list-modal';
import { MentionListModal } from '@/features/hey-syncly/ui/mention-list-modal/mention-list-modal';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

import StaticFeedbackModal from '../../Feedback/FeedbackDetail/FeedbackStaticDetail/StaticFeedbackModal';

const DetailModalStack = () => {
  const isPublic = usePublic();
  const currWorkspaceAlias = useWorkspaceStore(
    (state) => state.currWorkspace?.alias,
  );
  const { modalStack, setNewModal, setModalStack, closeModal, initModal } =
    useDetailModalStore((state) => ({
      modalStack: state.modalStack,
      setNewModal: state.setNewModal,
      setModalStack: state.setModalStack,
      closeModal: state.closeModal,
      initModal: state.initModal,
    }));
  const { workspace_id, feedback_alias, company_alias, customer_alias } =
    useParams();
  const location = useLocation();

  useEffect(() => {
    if (!workspace_id) return;

    if (feedback_alias) {
      setModalStack(DETAIL_MODAL_TYPE.FEEDBACK);
    } else if (company_alias) {
      setModalStack(DETAIL_MODAL_TYPE.COMPANY);
    } else if (customer_alias) {
      setModalStack(DETAIL_MODAL_TYPE.CUSTOMER);
    }

    return () => {
      if (!feedback_alias && !company_alias && !customer_alias) initModal();
    };
  }, [
    initModal,
    setModalStack,
    workspace_id,
    feedback_alias,
    company_alias,
    customer_alias,
    location.pathname,
  ]);

  return (
    <React.Fragment>
      {modalStack.map((modal, idx) => {
        const { type, alias, keys, options } = modal;
        const modalId = JSON.stringify({
          alias,
          type,
          keys,
        });
        switch (type) {
          case DETAIL_MODAL_TYPE.FEEDBACK: {
            if (isPublic) {
              return (
                <StaticFeedbackModal
                  key={idx}
                  alias={alias}
                  pos={idx * 600}
                  closeModal={() => closeModal(modalId)}
                  options={options}
                  onSelectNewModal={(alias: string, type: DETAIL_MODAL_TYPE) =>
                    setNewModal({
                      alias,
                      type,
                      idx: idx + 1,
                    })
                  }
                />
              );
            }

            if (!currWorkspaceAlias) {
              return null;
            }

            return (
              <FeedbackDetailProvider key={idx}>
                <FeedbackDetailModal
                  tableKey="feedback"
                  key={idx}
                  feedbackAlias={alias}
                  pos={idx * 600}
                  closeModal={() => closeModal(modalId)}
                  options={options}
                  onSelectNewModal={(alias: string, type: DETAIL_MODAL_TYPE) =>
                    setNewModal({
                      alias,
                      type,
                      idx: idx + 1,
                    })
                  }
                />
              </FeedbackDetailProvider>
            );
          }

          case DETAIL_MODAL_TYPE.STATIC_FEEDBACK:
            return (
              <StaticFeedbackModal
                key={idx}
                alias={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                options={options}
                onSelectNewModal={(alias: string, type: DETAIL_MODAL_TYPE) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );
          case DETAIL_MODAL_TYPE.COMPANY:
            return (
              <CompanyDetailModal
                key={idx}
                companyAlias={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                onSelectNewModal={(alias: string, type: DETAIL_MODAL_TYPE) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );
          case DETAIL_MODAL_TYPE.CUSTOMER:
            return (
              <CustomerDetailModal
                key={idx}
                customerId={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                onSelectNewModal={(alias: string, type: DETAIL_MODAL_TYPE) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );

          case DETAIL_MODAL_TYPE.AGENT:
            return (
              <AgentDetailModal
                key={idx}
                agentAlias={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                onSelectNewModal={(alias: string, type: DETAIL_MODAL_TYPE) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );
          case DETAIL_MODAL_TYPE.CHART:
            return (
              <ChartSegmentFeedbackListModal
                key={idx}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                query={alias}
                options={options}
                onSelectNewModal={(alias, type) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );

          case DETAIL_MODAL_TYPE.SEARCH_SUMMARY:
            return (
              <GlobalSearchSummaryFeedbackListModal
                key={idx}
                alias={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                options={options}
                onSelectNewModal={(alias, type) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );
          case DETAIL_MODAL_TYPE.FEEDBACK_LIST:
            return (
              <FeedbackListModal
                key={idx}
                alias={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                options={options}
                onSelectNewModal={(alias, type) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );
          case DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST:
            return (
              <FeedbackBlockListModal
                key={idx}
                alias={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                options={options}
                onSelectNewModal={(alias, type) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );
          case DETAIL_MODAL_TYPE.CHART_ANALYSIS:
            return (
              <ChartAnalysisFeedbackListModal
                key={idx}
                query={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                options={options}
                onSelectNewModal={(alias, type) =>
                  setNewModal({
                    alias,
                    type,
                    idx: idx + 1,
                  })
                }
              />
            );
          case DETAIL_MODAL_TYPE.HEY_SYNCLY_MENTION_LIST:
            return (
              <MentionListModal
                key={idx}
                alias={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                options={options}
                onSelectNewModal={(alias: string, blockAlias?: string) =>
                  setNewModal<{
                    focusedBlock?: string;
                  }>({
                    alias,
                    type: DETAIL_MODAL_TYPE.FEEDBACK,
                    idx: idx + 1,
                    options: {
                      focusedBlock: blockAlias,
                    },
                  })
                }
              />
            );
          case DETAIL_MODAL_TYPE.CATEGORY_MENTION_LIST:
            return (
              <CategoryMentionListModal
                key={idx}
                alias={alias}
                pos={idx * 600}
                closeModal={() => closeModal(modalId)}
                options={options}
                onSelectNewModal={(alias: string, blockAlias?: string) =>
                  setNewModal<{
                    focusedBlock?: string;
                  }>({
                    alias,
                    type: DETAIL_MODAL_TYPE.FEEDBACK,
                    idx: idx + 1,
                    options: {
                      focusedBlock: blockAlias,
                    },
                  })
                }
              />
            );
          default:
            return <></>;
        }
      })}
    </React.Fragment>
  );
};

export default DetailModalStack;
